import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

//========== Import components from MUI Library ============
//All Wrapper Elements
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Collapse from "@mui/material/Collapse";
import Fab from "@mui/material/Fab";
//All Loading related components
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
//All CSS
import { makeStyles } from "tss-react/mui";
//All Element/ Controls
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Fade from "@mui/material/Fade";
// All Icons
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
//========== Import components from our files ============
// All Common Element/ Controls
import { KMKBackButton } from "../../Common";
import CommonLayout from "../../Common/Layout";
import { setPrevState } from "../../../util/common";
// Others
import DataSourceClass from "../../../service/dataSource";
import { DATA_SOURCE } from "../../../util/Data/DataModeling";

const useStyles = makeStyles()({
  Header: {
    fontSize: "1.1rem",
    minHeight: "60px !important",
    backgroundColor: "#e8edf1",
    color: "black",
    marginTop: 0,
    borderRadius: 0,
    boxShadow: 0,
    fontWeight: 600,
  },
  HeaderTitle: {
    fontSize: 20,
    lineHeight: 1.5,
    paddingTop: 0,
    paddingRight: "20px",
    display: "inline-block",
  },
  SubHeading: {
    height: "25px",
    padding: "2px 16px",
    background: "#f0f0f0",
    position: "sticky",
    top: 0,
  },
  PanelContainer: {
    padding: "10px 30px",
    display: "flex",
    flexDirection: "column",
    width: "100%",
    // minHeight: "250px",
  },
  PanelRow: {
    padding: "10px 0",
  },
});

export default function ConfigCompare() {
  const { Workspace_id } = useParams();

  const { classes } = useStyles();
  const navigate = useNavigate();

  const workspace = useSelector((state) => state.workspace);

  const [loading, setLoading] = useState(false);
  const [versions, setVersions] = useState({});
  const [comparisonList, setcomparisonList] = useState({});
  const [type, setType] = useState("");
  const [viewType, setViewType] = useState({
    dataSource: "sidebyside",
    entity: "sidebyside",
  });
  const [collapseOpen, setCollapseOpen] = useState({
    sourceOpen: true,
    entityOpen: true,
  });

  const [sourceDiff, setSourceDiff] = useState({
    unified: "",
    sidebyside: "",
  });

  const [entityDiff, setEntityDiff] = useState({
    unified: "",
    sidebyside: "",
  });

  const [sourceAnchorEl, setSourceAnchorEl] = useState(null);
  const [entityAnchorEl, setEntityAnchorEl] = useState(null);
  const openSourceMenu = Boolean(sourceAnchorEl);
  const openEntityMenu = Boolean(entityAnchorEl);

  const { Name: workspaceName = "" } = useMemo(() => workspace?.workspace, [workspace.workspace]);

  const handleClick = (event, type) => {
    setType(type);
    event.stopPropagation();
    type === "dataSource" ? setSourceAnchorEl(event.currentTarget) : setEntityAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    event.stopPropagation();
    setSourceAnchorEl(null);
    setEntityAnchorEl(null);
  };

  const handleViewType = (viewType) => {
    setViewType((prev) => {
      return {
        dataSource: type === "dataSource" ? viewType : prev.dataSource,
        entity: type === "entity" ? viewType : prev.entity,
      };
    });
  };

  const MenuButtons = (invokedBy) => (
    <Grid sx={{ marginLeft: "auto" }}>
      <Button
        id={invokedBy + "_Button"}
        aria-controls={openSourceMenu || openEntityMenu ? invokedBy + "_Menu" : undefined}
        aria-haspopup="true"
        aria-expanded={
          invokedBy === "dataSource" ? (openSourceMenu ? "true" : undefined) : openEntityMenu ? "true" : undefined
        }
        onClick={(e) => handleClick(e, invokedBy)}
      >
        <MoreHorizIcon />
      </Button>
      <Menu
        id={invokedBy + "_Menu"}
        MenuListProps={{
          "aria-labelledby": invokedBy + "_Button",
        }}
        anchorEl={invokedBy === "dataSource" ? sourceAnchorEl : entityAnchorEl}
        open={invokedBy === "dataSource" ? openSourceMenu : openEntityMenu}
        onClose={handleClose}
        TransitionComponent={Fade}
        value={viewType}
      >
        <MenuItem
          selected={viewType[invokedBy] === "sidebyside"}
          onClick={(e) => {
            handleClose(e);
            handleViewType("sidebyside");
          }}
        >
          View Side-by-Side
        </MenuItem>
        <MenuItem
          selected={viewType[invokedBy] === "unified"}
          onClick={(e) => {
            handleClose(e);
            handleViewType("unified");
          }}
        >
          View Unified
        </MenuItem>
      </Menu>
    </Grid>
  );

  const addDiffComponent = useCallback(
    (diffArray, view = "sidebyside") =>
      view === "unified" ? (
        <>
          {diffArray &&
            diffArray.map((part, i) => (
              <Box
                key={i}
                style={{
                  backgroundColor: part.added ? "#E3FCEF" : part.removed ? "#FFEBE6" : "white",
                  padding: "2px 16px",
                }}
              >
                <pre style={{ margin: "0px" }}>{part.value}</pre>
              </Box>
            ))}
        </>
      ) : (
        <Grid
          container
          sx={{
            maxHeight: "80vh",
            overflow: "auto",
          }}
        >
          <Grid className={classes.SubHeading} borderRight="1px solid grey" borderLeft="1px solid white" size={6}>
            <Typography variant="body2">{`Config "#${versions?.configVersion?.Version_id}"`}</Typography>
          </Grid>
          <Grid className={classes.SubHeading} size={6}>
            <Typography variant="body2">{`Config "#${versions?.compareVersion?.Version_id}"`}</Typography>
          </Grid>
          {diffArray &&
            diffArray.map((part, i) => (
              <React.Fragment key={i}>
                <Grid
                  sx={{
                    backgroundColor: part.added ? "#F5F5F5" : part.removed ? "#FFEBE6" : "white",
                    borderRight: "1px solid grey",
                    padding: "2px 16px",
                  }}
                  size={6}
                >
                  {part.added ? <></> : <pre style={{ margin: "0px" }}>{part.value}</pre>}
                </Grid>
                <Grid
                  sx={{
                    backgroundColor: part.added ? "#E3FCEF" : part.removed ? "#F5F5F5" : "white",
                    padding: "2px 16px",
                  }}
                  size={6}
                >
                  {part.removed ? <></> : <pre style={{ margin: "0px" }}>{part.value}</pre>}
                </Grid>
              </React.Fragment>
            ))}
        </Grid>
      ),
    [classes.SubHeading, versions]
  );

  const handleBackClick = () => {
    window.localStorage.setItem("openHistory", true);
    window.localStorage.setItem("ConfigVersions_", JSON.stringify(versions));
    navigate(`/my-workspaces/${Workspace_id}/config`);
  };

  useEffect(() => {
    const compareConfigVersions = async () => {
      setLoading(true);
      let vs = window.localStorage.getItem("ConfigVersions");
      vs = JSON.parse(vs);

      if (vs && Object.keys(vs).length) {
        const { configVersion, compareVersion } = vs;

        const isBaselineLeft = configVersion.Version_id !== 0;
        const leftVersion = isBaselineLeft ? configVersion : compareVersion;
        const rightVersion = isBaselineLeft ? compareVersion : configVersion;

        setVersions({ configVersion: { ...leftVersion }, compareVersion: { ...rightVersion } });

        const dataSource = new DataSourceClass();
        const response = await dataSource.compareConfigVersions(
          Workspace_id,
          leftVersion.Version_id,
          rightVersion.Version_id
        );

        if (response.success) {
          setcomparisonList(response.data);
        }
      } else navigate(`/my-workspaces/${Workspace_id}/config`);
      setLoading(false);
    };
    compareConfigVersions();

    return () => {
      window.localStorage.removeItem("ConfigVersions");
    };
  }, [Workspace_id, navigate]);

  useEffect(() => {
    if (Object.keys(comparisonList).length !== 0) {
      setLoading(true);
      if (type === "") {
        setPrevState(setSourceDiff, {
          [viewType["dataSource"]]: addDiffComponent(comparisonList.sourceDiff, viewType["dataSource"]),
        });
        setPrevState(setEntityDiff, {
          [viewType["entity"]]: addDiffComponent(comparisonList.entityDiff, viewType["entity"]),
        });
      } else {
        let key = viewType[type];
        setSourceDiff((prev) =>
          type === "dataSource" && prev[key] === ""
            ? { ...prev, [key]: addDiffComponent(comparisonList.sourceDiff, key) }
            : prev
        );
        setEntityDiff((prev) =>
          type === "entity" && prev[key] === ""
            ? { ...prev, [key]: addDiffComponent(comparisonList.entityDiff, key) }
            : prev
        );
      }
      setLoading(false);
    }
    return () => {};
  }, [comparisonList, viewType, type, addDiffComponent]);

  const breadcrumbArr = [
    { link: "/my-workspaces", displayName: "Workspaces", active: false },
    { link: `/my-workspaces/${Workspace_id}`, displayName: workspaceName, active: false },
    { link: `/my-workspaces/${Workspace_id}/config`, displayName: "Apply to Data Warehouse", active: false },
    { link: `#`, displayName: "Config Comparison", active: true },
  ];

  return (
    <CommonLayout title="Config Comparison" breadcrumbArr={breadcrumbArr} loading={loading}>
      <Box
        component="main"
        sx={{
          backgroundColor: (theme) => {
            return theme.palette.TiffanyBlue.main;
          },
          flexGrow: 1,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Backdrop sx={{ backgroundColor: "transparent", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="primary" />
        </Backdrop>
        <AppBar
          sx={{
            position: "relative",
            boxShadow: 0,
            backgroundColor: "#e8edf1",
          }}
        >
          <Toolbar className={classes.Header}>
            <Grid container spacing={5} direction="row" alignItems={"center"}>
              <Grid size={{ xs: 12, sm: 12 }}>
                <Box
                  sx={{
                    display: "flex",
                    paddingRight: "20px",
                    alignItems: "center",
                    paddingTop: "10px",
                  }}
                >
                  <KMKBackButton handleClick={handleBackClick}></KMKBackButton>
                  <Typography className={classes.HeaderTitle} component="div">
                    {`Comparing Config "#${versions?.configVersion?.Version_id}" & "#${versions?.compareVersion?.Version_id}"`}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>

        <Box sx={{ flex: 1, overflowY: "auto" }}>
          <Grid container direction="column" justifyContent="flex-end" alignItems="left">
            <Grid className={classes.PanelContainer}>
              <Grid container className={classes.PanelRow} spacing={5}>
                <Grid size={12}>
                  <TableContainer sx={{ marginBottom: "100px" }}>
                    <Table stickyHeader sx={{ width: "100%" }} size="small">
                      <TableBody>
                        <TableRow
                          style={{ cursor: "pointer", backgroundColor: "white" }}
                          onClick={() => setPrevState(setCollapseOpen, { sourceOpen: !collapseOpen.sourceOpen })}
                          tabIndex={-1}
                          hover={true}
                          className={classes.tr}
                        >
                          <TableCell component="th" scope="row" padding="normal">
                            <Grid container sx={{ display: "flex", alignItems: "center", margin: "2px 0px" }}>
                              <Grid>
                                <Fab
                                  edge="start"
                                  size="small"
                                  sx={{
                                    backgroundColor: "white",
                                    width: "20px",
                                    height: "20px",
                                    minHeight: "0px",
                                    marginRight: "15px",
                                  }}
                                >
                                  {collapseOpen.sourceOpen === true ? (
                                    <KeyboardArrowDownIcon />
                                  ) : (
                                    <KeyboardArrowRightIcon />
                                  )}
                                </Fab>
                              </Grid>
                              <Grid alignItems="center">
                                <Typography variant="body2" sx={{ fontWeight: "750" }}>
                                  {DATA_SOURCE.singular}
                                </Typography>
                              </Grid>
                              {MenuButtons("dataSource")}
                            </Grid>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ margin: 0, padding: 0 }}>
                            <Collapse in={collapseOpen.sourceOpen === true} timeout={1} sx={{ background: "white" }}>
                              {sourceDiff[viewType["dataSource"]]}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ height: "20px" }}></TableRow>
                        <TableRow
                          style={{ cursor: "pointer", backgroundColor: "white" }}
                          onClick={() => setPrevState(setCollapseOpen, { entityOpen: !collapseOpen.entityOpen })}
                          tabIndex={-1}
                          hover={true}
                          className={classes.tr}
                        >
                          <TableCell component="th" scope="row" padding="normal">
                            <Grid container sx={{ display: "flex", alignItems: "center", margin: "2px 0px" }}>
                              <Grid>
                                <Fab
                                  edge="start"
                                  size="small"
                                  sx={{
                                    backgroundColor: "white",
                                    width: "20px",
                                    height: "20px",
                                    minHeight: "0px",
                                    marginRight: "15px",
                                  }}
                                >
                                  {collapseOpen.entityOpen === true ? (
                                    <KeyboardArrowDownIcon />
                                  ) : (
                                    <KeyboardArrowRightIcon />
                                  )}
                                </Fab>
                              </Grid>
                              <Grid alignItems="center">
                                <Typography variant="body2" sx={{ fontWeight: "750" }}>
                                  Entity
                                </Typography>
                              </Grid>
                              {MenuButtons("entity")}
                            </Grid>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ margin: 0, padding: 0 }}>
                            <Collapse in={collapseOpen.entityOpen === true} timeout={1} sx={{ background: "white" }}>
                              {entityDiff[viewType["entity"]]}
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </CommonLayout>
  );
}
